import { useEffect, useState } from "react";
import { Brain, Plus, Pencil, Archive, X, Check } from "lucide-react";
import { CtaButton, MonoLabel } from "./kit";
import api from "../lib/api";

const KIND_OPTIONS = [
  { value: "preference",       label: "Preference" },
  { value: "correction",       label: "Correction" },
  { value: "rejected_pattern", label: "Rejected pattern" },
  { value: "business_fact",    label: "Business fact" },
  { value: "content_rule",     label: "Content rule" },
];

const MODULE_OPTIONS = [
  { value: "",                label: "All modules" },
  { value: "strategy",        label: "Strategy" },
  { value: "action_plan",     label: "Action Plan" },
  { value: "calendar",        label: "Calendar" },
  { value: "script_lab",      label: "Script Lab" },
  { value: "carousel",        label: "Carousel" },
  { value: "creative_studio", label: "Creative Studio" },
  { value: "landing",         label: "Landing" },
  { value: "panel",           label: "Panel" },
];

const KIND_LABEL = Object.fromEntries(KIND_OPTIONS.map(o => [o.value, o.label]));
const MODULE_LABEL = Object.fromEntries(MODULE_OPTIONS.filter(o => o.value).map(o => [o.value, o.label]));

// Compact readout of the memory summary — never shows ObjectIds, dedupe_keys, confidence.
function MemorySummary({ memory }) {
  if (!memory) return null;
  const bf = memory.brand_facts || {};
  const tn = memory.tone || {};
  const cr = memory.content_rules || {};
  const fmt = (v) => Array.isArray(v) ? v.filter(Boolean).join(" · ") : v;
  const row = (label, value) => {
    const s = fmt(value);
    if (!s) return null;
    return (
      <div key={label} className="flex items-baseline gap-3 py-1.5 text-[13px]" data-testid={`memory-summary-${label.toLowerCase().replace(/\s+/g,'-')}`}>
        <span className="w-40 shrink-0 text-[10.5px] font-bold tracking-[0.1em] uppercase text-[#9A9591]">{label}</span>
        <span className="text-[#181621]">{s}</span>
      </div>
    );
  };
  return (
    <div className="mt-5 border-l-[3px] border-l-[#E7E2D6] pl-4">
      {row("Brand name", bf.brand_name)}
      {row("Industry", bf.industry)}
      {row("Positioning", bf.positioning)}
      {row("Value prop", bf.value_proposition)}
      {row("Products", bf.products_services)}
      {row("Audience", bf.target_audience)}
      {row("Objectives", bf.objectives)}
      {row("Voice", tn.voice)}
      {row("Preferred words", cr.preferred_words)}
      {row("Forbidden words", cr.forbidden_words)}
      {row("Preferred CTAs", cr.preferred_ctas)}
    </div>
  );
}

function ItemRow({ item, onEdit, onArchive }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(item.text);
  const [importance, setImportance] = useState(item.importance);
  const [module, setModule] = useState(item.module || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onEdit(item.id, {
        text: text.trim(),
        importance: Number(importance) || 5,
        module: module || null,
      });
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <div className="rounded-xl border border-[#EAE5DB] bg-white p-4" data-testid={`memory-item-edit-${item.id}`}>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={2000}
          className="w-full text-[13.5px] text-[#181621] border border-[#EAE5DB] rounded-lg px-3 py-2 focus:outline-none focus:border-[#181621]"
          rows={3}
          data-testid={`memory-item-edit-text-${item.id}`}
        />
        <div className="flex flex-wrap gap-3 mt-3 items-center">
          <label className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#54505B]">Module
            <select value={module} onChange={(e) => setModule(e.target.value)}
              className="ml-2 text-[12px] border border-[#EAE5DB] rounded-md px-2 py-1 bg-white"
              data-testid={`memory-item-edit-module-${item.id}`}>
              {MODULE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <label className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#54505B]">Importance
            <input type="number" min={1} max={10} value={importance}
              onChange={(e) => setImportance(e.target.value)}
              className="ml-2 w-16 text-[12px] border border-[#EAE5DB] rounded-md px-2 py-1"
              data-testid={`memory-item-edit-importance-${item.id}`} />
          </label>
          <div className="ml-auto flex gap-2">
            <button onClick={() => setEditing(false)}
              className="inline-flex items-center gap-1 text-[12px] text-[#54505B] hover:text-[#181621] px-3 py-1.5"
              data-testid={`memory-item-cancel-${item.id}`}>
              <X size={12} /> Cancel
            </button>
            <CtaButton className="!py-1.5 !px-3 text-[12px]" onClick={save} disabled={busy}
              data-testid={`memory-item-save-${item.id}`}>
              <Check size={12} /> {busy ? "Saving…" : "Save"}
            </CtaButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-[#EAE5DB] bg-white p-4 hover:border-[#D9D3C6] transition-colors" data-testid={`memory-item-${item.id}`}>
      <div className="flex flex-wrap gap-2 items-center">
        <span className="inline-block text-[10px] font-bold uppercase tracking-[0.1em] text-[#181621] bg-[#F3EFE7] rounded-full px-2 py-0.5">
          {KIND_LABEL[item.kind] || item.kind}
        </span>
        {item.module && (
          <span className="inline-block text-[10px] font-bold uppercase tracking-[0.1em] text-[#54505B] border border-[#EAE5DB] rounded-full px-2 py-0.5">
            {MODULE_LABEL[item.module] || item.module}
          </span>
        )}
        <span className="ml-auto text-[10.5px] tabular-nums font-bold text-[#9A9591]">Importance {item.importance}/10</span>
      </div>
      <p className="text-[13.5px] text-[#181621] leading-relaxed mt-2">{item.text}</p>
      <div className="flex gap-2 mt-3">
        <button onClick={() => setEditing(true)}
          className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#54505B] hover:text-[#181621] px-2 py-1"
          data-testid={`memory-item-edit-btn-${item.id}`}>
          <Pencil size={11} /> Edit
        </button>
        <button onClick={() => onArchive(item.id)}
          className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#9A9591] hover:text-[#E0603A] px-2 py-1"
          data-testid={`memory-item-archive-btn-${item.id}`}>
          <Archive size={11} /> Archive
        </button>
      </div>
    </div>
  );
}

function AddItemForm({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("preference");
  const [module, setModule] = useState("");
  const [text, setText] = useState("");
  const [importance, setImportance] = useState(7);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const t = text.trim();
    if (!t) return;
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/project-memory/items", {
        kind, module: module || null, text: t, importance: Number(importance) || 7,
      });
      onCreated(data);
      setText(""); setImportance(7); setModule(""); setKind("preference"); setOpen(false);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't save — try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 text-[12.5px] font-bold text-[#181621] border border-dashed border-[#D9D3C6] hover:border-[#181621] rounded-lg px-3 py-2 transition-colors"
        data-testid="memory-add-btn">
        <Plus size={13} /> Teach Agenzee something new
      </button>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-xl border border-[#EAE5DB] bg-white p-4 mt-2" data-testid="memory-add-form">
      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <label className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#54505B]">Type
          <select value={kind} onChange={(e) => setKind(e.target.value)}
            className="mt-1 block w-full text-[13px] border border-[#EAE5DB] rounded-md px-2 py-1.5 bg-white"
            data-testid="memory-add-kind">
            {KIND_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        <label className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#54505B]">Module
          <select value={module} onChange={(e) => setModule(e.target.value)}
            className="mt-1 block w-full text-[13px] border border-[#EAE5DB] rounded-md px-2 py-1.5 bg-white"
            data-testid="memory-add-module">
            {MODULE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
      </div>
      <label className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#54505B]">Instruction / knowledge
        <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} rows={3} required
          className="mt-1 block w-full text-[13.5px] border border-[#EAE5DB] rounded-lg px-3 py-2 focus:outline-none focus:border-[#181621]"
          placeholder="Example: Always mention the town where we bake, and never use the word 'artisanal'."
          data-testid="memory-add-text" />
      </label>
      <div className="flex flex-wrap items-center gap-3 mt-3">
        <label className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#54505B]">Importance
          <input type="number" min={1} max={10} value={importance}
            onChange={(e) => setImportance(e.target.value)}
            className="ml-2 w-16 text-[12px] border border-[#EAE5DB] rounded-md px-2 py-1"
            data-testid="memory-add-importance" />
          <span className="ml-2 text-[10.5px] text-[#9A9591]">1–10, default 7</span>
        </label>
        <div className="ml-auto flex gap-2">
          <button type="button" onClick={() => { setOpen(false); setError(""); }}
            className="text-[12px] text-[#54505B] hover:text-[#181621] px-3 py-1.5"
            data-testid="memory-add-cancel">Cancel</button>
          <CtaButton type="submit" disabled={busy || !text.trim()} className="!py-1.5 !px-4 text-[12.5px]"
            data-testid="memory-add-save">
            {busy ? "Saving…" : "Save to memory"}
          </CtaButton>
        </div>
      </div>
      {error && <p className="mt-2 text-[12px] text-[#E0603A]" data-testid="memory-add-error">{error}</p>}
    </form>
  );
}

export function WhatAgenzeeKnows() {
  const [state, setState] = useState({ loading: true, error: null, project: null, memory: null, items: [] });

  const load = () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    api.get("/project-memory")
      .then(({ data }) => setState({ loading: false, error: null, project: data.project, memory: data.memory, items: data.items || [] }))
      .catch((err) => {
        // Feature flag disabled → 404. Silently hide the card.
        if (err?.response?.status === 404) {
          setState({ loading: false, error: "disabled", project: null, memory: null, items: [] });
        } else {
          setState({ loading: false, error: err?.response?.data?.detail || "Couldn't load memory.", project: null, memory: null, items: [] });
        }
      });
  };
  useEffect(() => { load(); }, []);

  const onCreated = (newItem) => {
    setState((s) => ({ ...s, items: [newItem, ...s.items.filter(x => x.id !== newItem.id)] }));
  };

  const onEdit = async (id, patch) => {
    const { data } = await api.patch(`/project-memory/items/${id}`, patch);
    setState((s) => ({ ...s, items: s.items.map(x => x.id === id ? data : x) }));
  };

  const onArchive = async (id) => {
    await api.delete(`/project-memory/items/${id}`);
    setState((s) => ({ ...s, items: s.items.filter(x => x.id !== id) }));
  };

  // Feature flag off — hide the whole card
  if (state.error === "disabled") return null;

  return (
    <div className="card-soft mt-5 p-6 sm:p-8" data-testid="what-agenzee-knows-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-full bg-[#181621] flex items-center justify-center">
            <Brain size={15} strokeWidth={2} className="text-white" />
          </span>
          <div>
            <MonoLabel>WHAT AGENZEE KNOWS</MonoLabel>
            <p className="text-[13px] text-[#54505B] mt-1.5 max-w-lg">
              Preferences, corrections and facts Agenzee applies to every plan and content it produces for your brand.
            </p>
          </div>
        </div>
      </div>

      {state.loading ? (
        <div className="h-24 mt-6 rounded-xl bg-[#F3EFE7] animate-pulse" data-testid="what-knows-loading" />
      ) : state.error && state.error !== "disabled" ? (
        <p className="text-[13px] text-[#E0603A] mt-5" data-testid="what-knows-error">{state.error}</p>
      ) : (
        <>
          <MemorySummary memory={state.memory} />

          <div className="mt-6">
            <div className="flex items-center justify-between gap-3">
              <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#9A9591]" data-testid="what-knows-count">
                {state.items.length} active {state.items.length === 1 ? "note" : "notes"}
              </p>
            </div>
            {state.items.length > 0 && (
              <div className="mt-3 grid gap-2.5" data-testid="what-knows-items">
                {state.items.map((it) => (
                  <ItemRow key={it.id} item={it} onEdit={onEdit} onArchive={onArchive} />
                ))}
              </div>
            )}
            <div className="mt-4">
              <AddItemForm onCreated={onCreated} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default WhatAgenzeeKnows;
